/**
 * OperationalBundleReplayWorkspace
 *
 * Replays a verified operational bundle by rendering the reconstructed
 * OperationalWorkspaceState using the existing GovernanceWorkspaceShell.
 *
 * INVARIANTS:
 * - Reuses GovernanceWorkspaceShell — no duplicated pane rendering logic
 * - Read-only: no mutation callbacks have side effects
 * - Disconnected from execution authority and backend
 */

import React, { useState, useCallback } from 'react';
import type { OperationalWorkspaceState } from '../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { replayCursorStyle } from '../governanceWorkspace/motion/replayMotionSemantics.js';
import { GovernanceWorkspaceShell } from '../governanceWorkspace/GovernanceWorkspaceShell.js';
import { GovernanceWorkspaceStatusBar } from '../governanceWorkspace/GovernanceWorkspaceStatusBar.js';

interface OperationalBundleReplayWorkspaceProps {
  workspace: OperationalWorkspaceState;
  isFixture: boolean;
}

export const OperationalBundleReplayWorkspace: React.FC<OperationalBundleReplayWorkspaceProps> = ({
  workspace,
  isFixture,
}) => {
  // Local selection state for replay interactivity
  const [replayWorkspace, setReplayWorkspace] = useState<OperationalWorkspaceState>(workspace);

  const handleSelectEvent = useCallback((eventId: string) => {
    setReplayWorkspace((prev) => ({
      ...prev,
      selection: { ...prev.selection, selectedEventId: eventId },
    }));
  }, []);

  const handleToggleSpan = useCallback((spanId: string) => {
    setReplayWorkspace((prev) => ({
      ...prev,
      selection: { ...prev.selection, selectedSpanId: spanId },
    }));
  }, []);

  const handleSelectTransition = useCallback((transitionId: string) => {
    setReplayWorkspace((prev) => ({
      ...prev,
      selection: { ...prev.selection, selectedContinuityTransitionId: transitionId },
    }));
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: NEUTRAL.bg100,
      overflow: 'hidden',
    }}>
      {/* Replay mode indicator */}
      <div
        style={{
          height: 3,
          width: '100%',
          ...replayCursorStyle(true, '#4868a0'),
        }}
        data-testid="replay-cursor-indicator"
      />
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <GovernanceWorkspaceShell
          workspace={replayWorkspace}
          onSelectEvent={handleSelectEvent}
          onToggleSpan={handleToggleSpan}
          onSelectTransition={handleSelectTransition}
        />
      </div>
      <GovernanceWorkspaceStatusBar workspace={replayWorkspace} isFixture={isFixture} />
    </div>
  );
};
